import React, { useMemo, useState } from "react";
import Modal from "react-modal";
import { toast } from "react-toastify";
import axiosInstance from "../../common/axios";
import useGet from "../../hooks/useGet";
import { getErrors, toastDefault } from "../../utils/handler.utils";
import UiHandler from "../UiHandler";

Modal.setAppElement("#root");

function BulkVoucher(props) {
  const productId = props.match.params.id;
  const [uniqueState, setUniqueState] = useState(false);
  const [voucherText, setVoucherText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [packages, loading, error] = useGet(
    `admin/topup-packages/${productId}`,
    "",
    uniqueState
  );

  const voucherLines = useMemo(
    () =>
      voucherText
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean),
    [voucherText]
  );

  const sortedPackages = useMemo(
    () =>
      (packages || [])
        .filter((pkg) => pkg?.voucher_prefix)
        .sort(
          (a, b) =>
            (b.voucher_prefix || "").length - (a.voucher_prefix || "").length
        ),
    [packages]
  );

  const groupedVouchers = useMemo(() => {
    const grouped = new Map();
    const unknown = [];

    if (!sortedPackages.length || !voucherLines.length) {
      return { grouped, unknown };
    }

    voucherLines.forEach((line) => {
      const matchedPackage = sortedPackages.find((pkg) =>
        line.startsWith(pkg.voucher_prefix)
      );

      if (matchedPackage) {
        if (!grouped.has(matchedPackage.id)) {
          grouped.set(matchedPackage.id, []);
        }
        grouped.get(matchedPackage.id).push(line);
      } else {
        unknown.push(line);
      }
    });

    return { grouped, unknown };
  }, [sortedPackages, voucherLines]);

  const previewRows = useMemo(
    () =>
      (packages || [])
        .map((pkg) => ({
          id: pkg.id,
          name: pkg.name,
          prefix: pkg.voucher_prefix || "-",
          count: groupedVouchers.grouped.get(pkg.id)?.length || 0,
        }))
        .filter((row) => row.count > 0),
    [packages, groupedVouchers]
  );

  const insertVouchers = (e) => {
    e.preventDefault();

    if (!voucherLines.length) {
      toast.error("Please add voucher data", toastDefault);
      return;
    }

    if (!packages?.length) {
      toast.error("No packages found", toastDefault);
      return;
    }

    const requests = [];
    groupedVouchers.grouped.forEach((items, packageId) => {
      if (!items.length) return;
      requests.push(
        axiosInstance.post(`/admin/packages/add-voucher`, {
          data: items,
          package_id: packageId,
        })
      );
    });

    if (!requests.length) {
      toast.error("No voucher matched any package prefix", toastDefault);
      return;
    }

    setIsSubmitting(true);
    const request = Promise.all(requests);

    toast.promise(
      request,
      {
        pending: "Uploading vouchers...",
        error: {
          render(err) {
            return getErrors(err?.data || err, false, true);
          },
        },
        success: {
          render() {
            return "Vouchers uploaded successfully";
          },
        },
      },
      toastDefault
    );

    request
      .then(() => {
        setVoucherText("");
        setUniqueState((prev) => !prev);
      })
      .catch(() => {})
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <section className="relative container_admin">
      <div className="bg-white overflow-hidden rounded">
        <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between flex-wrap">
          <h3 className="text-lg font-bold text-black">Bulk Voucher</h3>
        </div>

        <div className="relative min-h-[100px]">
          <UiHandler data={packages} loading={loading} error={error} />
        </div>

        <div className="p-3">
          <form onSubmit={insertVouchers}>
            <div className="form_grid">
              <div>
                <label htmlFor="voucher_data">
                  Voucher Data (Detect {voucherLines.length})
                </label>
                <textarea
                  id="voucher_data"
                  className="form_input"
                  placeholder="Voucher Data"
                  value={voucherText}
                  onChange={(e) => setVoucherText(e.target.value)}
                  required
                />
                {groupedVouchers.unknown.length > 0 && (
                  <p className="text-xs text-red-600 mt-1">
                    Unmatched vouchers: {groupedVouchers.unknown.length}
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                className="cstm_btn"
                disabled={isSubmitting}
              >
                Insert Voucher
              </button>
              <button
                type="button"
                className="cstm_btn"
                onClick={() => setIsPreviewOpen(true)}
              >
                Preview
              </button>
            </div>
          </form>
        </div>
      </div>

      <Modal
        isOpen={isPreviewOpen}
        onRequestClose={() => setIsPreviewOpen(false)}
        style={{
          overlay: {
            background: "rgba(0,0,0,0.7)",
          },
        }}
      >
        <div className="py-2 px-3.5 md:py-2 md:px-5 border-b border-gray-300 flex items-center justify-between">
          <h2 className="font-bold text-lg text-gray-700">Voucher Preview</h2>
          <button
            type="button"
            className="cstm_btn_small"
            onClick={() => setIsPreviewOpen(false)}
          >
            Close
          </button>
        </div>
        <div className="p-3">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-gray-600 border-b">
                  <th className="py-2 pr-3">Package</th>
                  <th className="py-2 pr-3">Prefix</th>
                  <th className="py-2">Count</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row) => (
                  <tr key={row.id} className="border-b">
                    <td className="py-2 pr-3">{row.name}</td>
                    <td className="py-2 pr-3">{row.prefix}</td>
                    <td className="py-2">{row.count}</td>
                  </tr>
                ))}
                {groupedVouchers.unknown.length > 0 && (
                  <tr>
                    <td className="py-2 pr-3 font-semibold">Unmatched</td>
                    <td className="py-2 pr-3">-</td>
                    <td className="py-2">{groupedVouchers.unknown.length}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>
    </section>
  );
}

export default BulkVoucher;
